import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { useSimDetails, useSimMostRecentSession } from 'api';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useUserInfo from 'hooks/User/useUserInfo';
import type { FC } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import useSimUrl from 'utils/hooks/useSimUrl';
import SimMostRecentSessionInfo from './SimMostRecentSessionInfo';
import styles from './SimStatusPanel.module.css';

interface SimStatusPanelProps {
  hologramSimId?: HologramSimId;
  simDetailBaseUrl: string;
  usageExitedTestingUrl: string;
}

const SimStatusPanel: FC<SimStatusPanelProps> = ({
  hologramSimId,
  simDetailBaseUrl,
  usageExitedTestingUrl,
}) => {
  // TODO: consider whether this should be it's own control to keep it separate from the loading logic

  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
  } = useSimDetails(hologramSimId);

  const { isLoading: mostRecentLoading, error: mostRecentError } =
    useSimMostRecentSession(hologramSimId);

  const organizationId = useOrganization();
  const url = useSimUrl({ simDetailBaseUrl, orgId: organizationId, simId: hologramSimId });

  const error = simDetailsError || mostRecentError;
  const isLoading = simDetailsIsLoading || mostRecentLoading;

  let panelContents;
  if (error) {
    panelContents = (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading your SIM"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  } else if (isLoading) {
    panelContents = <PanelNotification>Loading...</PanelNotification>;
  } else {
    panelContents = (
      <div className={styles.panelSection}>
        <SimMostRecentSessionInfo
          hologramSimId={hologramSimId}
          usageExitedTestingUrl={usageExitedTestingUrl}
        />
      </div>
    );
  }

  const { releaseFlags } = useUserInfo();

  const cellTowerLocationText = releaseFlags?.has(ReleaseFlag.HIDE_TOWER_LOCATIONS)
    ? undefined
    : ' Cell tower location is an approximate crowd-sourced location based on the last session.';

  return (
    <Panel
      header="Last connection"
      headerActions={
        <Button variant="tertiary" iconEnd="ArrowEastShort" to={`${url}/usage`} noPadding>
          Session history
        </Button>
      }
      noContentPadding
      footer={
        simDetails.profileName?.includes('USA-2')
          ? `Session and status data for the enabled profile is reported at most every 2 hours.${cellTowerLocationText ?? ''}`
          : cellTowerLocationText
      }
      footerVariant="alternative"
      className={styles.wrapper}
      isLoading={isLoading}
    >
      {panelContents}
    </Panel>
  );
};
export default SimStatusPanel;
